import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import api from "../../../../../api";
import LocationDropdown from "../../../tabs/Dropdown/LocationName";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CasesTransportedBarChart = ({ year, locationId, onSummaryUpdate, setExportFunctions }) => {
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [noData, setNoData] = useState(false);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/ems/case-transported", {
          params: { year, location_id: locationId || "" },
        });

        const { monthly_cases, today_cases, this_week_cases, this_month_cases } = response.data;

        onSummaryUpdate({
          today: today_cases,
          week: this_week_cases,
          month: this_month_cases,
        });

        if (monthly_cases?.cases && monthly_cases.cases.length > 0) {
          setChartData({
            labels: monthly_cases.months,
            data: monthly_cases.cases,
          });
          const allZero = monthly_cases.cases.every((v) => v === 0);
          setNoData(allZero);
        } else {
          setChartData({ labels: [], data: [] });
          setNoData(true);
        }
      } catch (error) {
        console.error("Error fetching cases transported:", error);
        setChartData({ labels: [], data: [] });
        setNoData(true);
        onSummaryUpdate({ today: 0, week: 0, month: 0 });
      }
    };

    fetchData();
  }, [year, locationId, onSummaryUpdate]);

  const exportPDF = async () => {
    try {
      const dataURI = await ApexCharts.exec("cases-transported", "dataURI", {
        scale: 2,
        width: 800,
      });

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;

      // Add chart image
      const chartHeight = 100;
      const chartWidth = pageWidth - 2 * margin;
      const chartTopMargin = 40;
      doc.addImage(dataURI.imgURI, "PNG", margin, chartTopMargin, chartWidth, chartHeight);

      // Define table options
      const options = {
        startY: chartTopMargin + chartHeight + 10,
        head: [["Month", "Cases Transported"]],
        body: chartData.labels.map((label, index) => [
          label,
          chartData.data[index]?.toFixed(0) || "0",
        ]),
        theme: "grid",
        headStyles: {
          fillColor: [0, 123, 255], // Blue header (#007bff)
          textColor: 255,
          fontStyle: "bold",
          fontSize: 10,
        },
        styles: {
          fontSize: 9,
          cellPadding: 2,
          overflow: "linebreak",
          minCellWidth: 30,
        },
        columnStyles: {
          0: { cellWidth: (pageWidth - 2 * margin) * 0.6 }, // Month column wider
          1: { cellWidth: (pageWidth - 2 * margin) * 0.4 }, // Cases column narrower
        },
        margin: { left: margin, right: margin },
        didDrawPage: (data) => {
          // Header - Main title
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text("Cases Transported Report", pageWidth / 2, 15, {
            align: "center",
          });

          // Header - Period information
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(
            `Year: ${year}${locationId ? `, Location ID: ${locationId}` : ''}`,
            pageWidth / 2,
            25,
            { align: "center" }
          );

          // Header - Generated timestamp
          doc.setFontSize(8);
          doc.text(
            "Generated on: October 10, 2025, 11:14 AM IST",
            pageWidth / 2,
            33,
            { align: "center" }
          );

          // Footer - Page number
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
          );
        },
      };

      // Add table only if data exists
      if (chartData.labels.length > 0 && !noData) {
        autoTable(doc, options);
      } else {
        // Add message if no data
        doc.setFontSize(10);
        doc.text(
          "No data available for the selected period.",
          pageWidth / 2,
          chartTopMargin + chartHeight + 20,
          { align: "center" }
        );
      }

      doc.save(`cases_transported_${year}${locationId ? `_loc${locationId}` : ''}.pdf`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
    }
  };

  const exportXML = () => {
    let xmlData = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xmlData += `<CasesTransported year="${year}"${locationId ? ` location_id="${locationId}"` : ''}>\n`;
    
    if (chartData.labels.length > 0 && !noData) {
      chartData.labels.forEach((label, index) => {
        xmlData += `  <Month>\n`;
        xmlData += `    <Label>${label}</Label>\n`;
        xmlData += `    <Cases>${chartData.data[index]?.toFixed(0) || '0'}</Cases>\n`;
        xmlData += `  </Month>\n`;
      });
    } else {
      xmlData += `  <Message>No data available for the selected period.</Message>\n`;
    }
    xmlData += `</CasesTransported>`;

    const blob = new Blob([xmlData], { type: "application/xml" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `cases_transported_${year}${locationId ? `_loc${locationId}` : ''}.xml`;
    link.click();
  };

  const downloadGraph = async () => {
    try {
      const dataURI = await ApexCharts.exec("cases-transported", "dataURI", {
        scale: 2,
        width: 1200,
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height + 70; // Extra space for title and timestamp

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = '#000000';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Cases Transported', canvas.width / 2, 30);

        ctx.font = '16px Arial';
        ctx.fillText(
          `Year: ${year}${locationId ? `, Location ID: ${locationId}` : ''}`,
          canvas.width / 2,
          50
        );

        ctx.font = '12px Arial';
        ctx.fillText('Generated on: October 10, 2025, 11:14 AM IST', canvas.width / 2, 65);

        ctx.drawImage(img, 0, 75, img.width, img.height);

        const link = document.createElement('a');
        link.download = `cases_transported_${year}${locationId ? `_loc${locationId}` : ''}.png`;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      img.src = dataURI.imgURI;
    } catch (error) {
      console.error("Error downloading graph:", error);
    }
  };

  useEffect(() => {
    setExportFunctions({ exportPDF, exportXML, downloadGraph });
  }, [setExportFunctions, chartData, chartInstance]);

  useEffect(() => {
    const chartContainer = document.querySelector("#cases-transported");

    if (chartContainer) {
      const maxDataValue = Math.max(...chartData.data, 10);
      const maxYAxisValue = Math.ceil(maxDataValue / 5) * 5;

      const options = {
        series: [{ name: "Cases Transported", data: chartData.data }],
        chart: {
          id: "cases-transported",
          height: 265,
          type: "bar",
          fontFamily: "Plus Jakarta Sans, sans-serif",
          foreColor: "#adb0bb",
          toolbar: {
            show: false,
            export: {
              png: {
                filename: `cases_transported_${year}${locationId ? `_loc${locationId}` : ''}`,
              },
            },
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
          events: {
            mounted: function(chartContext, config) {
              setChartInstance(chartContext);
            },
            updated: function(chartContext, config) {
              setChartInstance(chartContext);
            },
          },
        },
        colors: ["#007bff"],
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "40%",
            borderRadiusApplication: "end",
            dataLabels: {
              position: 'top',
            },
          },
        },
        stroke: {
          show: false,
          width: 2,
          colors: ['transparent'],
        },
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return val ? val.toFixed(0) : '0';
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ["#304758"],
          },
        },
        legend: { show: false },
        grid: {
          show: false,
          borderColor: '#e0e0e0',
          strokeDashArray: 3,
        },
        yaxis: {
          min: 0,
          max: maxYAxisValue,
          tickAmount: 4,
          labels: {
            formatter: (val) => val.toFixed(0),
            style: {
              colors: '#6b7280',
              fontSize: '12px',
            },
          },
          title: {
            text: 'Cases Transported',
            style: {
              color: '#6b7280',
              fontSize: '12px',
            },
          },
        },
        xaxis: {
          categories: chartData.labels,
          axisTicks: { show: false },
          labels: {
            style: {
              colors: '#6b7280',
              fontSize: '12px',
            },
          },
        },
        tooltip: {
          theme: "dark",
          x: { show: true },
          y: {
            formatter: function(val) {
              return val ? `${val.toFixed(0)} cases` : '0 cases';
            },
          },
          style: {
            fontSize: '12px',
          },
        },
        states: {
          hover: {
            filter: {
              type: 'darken',
              value: 0.8,
            },
          },
        },
      };

      const chart = new ApexCharts(chartContainer, options);
      chart.render();
      setChartInstance(chart);

      return () => {
        chart.destroy();
        setChartInstance(null);
      };
    }
  }, [chartData, year, locationId]);

  return (
    <>
      <div id="cases-transported"></div>
      {noData && (
        <div className="text-center mt-3 fw-semibold text-muted">
          Data is Not Available
        </div>
      )}
    </>
  );
};

const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

const CasesTransportedCard = () => {
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    location_id: "",
  });

  const [summary, setSummary] = useState({
    today: 0,
    week: 0,
    month: 0,
  });

  const [exportFunctions, setExportFunctions] = useState({
    exportPDF: () => {},
    exportXML: () => {},
    downloadGraph: () => {},
  });

  const handleFilterChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="card w-100 p-4 position-relative overflow-hidden mb-0 h-100">
      <div className="card-body">
        <div className="mb-4">
          <h5 className="card-title mb-0 fw-semibold w-100 ">Cases Transported</h5>
          <div className="d-flex align-items-center gap-2 w-100 mt-3">
            <div className="row d-flex w-100">
              <div className="col-lg-6 my-2">
                <select
                  className="form-select text-dark"
                  value={filters.year}
                  onChange={(e) => setFilters({ ...filters, year: Number(e.target.value) })}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-6 my-2">
                <LocationDropdown
                  value={filters.location_id}
                  onChange={handleFilterChange}
                  name="location_id"
                />
              </div>
            </div>
            <div className="px-3">
              <div className="dropdown">
                <button
                  className="btn btn-outline-primary dropdown-toggle"
                  type="button"
                  id="exportDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Export Options
                </button>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="exportDropdown">
                  <li>
                    <button className="dropdown-item" onClick={exportFunctions.exportPDF}>
                      Export PDF
                    </button>
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={exportFunctions.exportXML}>
                      Export XML
                    </button>
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={exportFunctions.downloadGraph}>
                      Download Graph
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <CasesTransportedBarChart
          year={filters.year}
          locationId={filters.location_id}
          onSummaryUpdate={setSummary}
          setExportFunctions={setExportFunctions}
        />

        <div className="d-flex align-items-center justify-content-center flex-wrap mt-3">
          <div className="d-flex align-items-center justify-content-center p-2">
            <span className="round-8 text-bg-primary rounded-circle me-2 d-inline-block"></span>
            <h6 className="mb-0">
              <strong>Today:</strong>{" "}
              <span className="text-muted">{summary.today} Cases</span>
            </h6>
          </div>
          <div className="d-flex align-items-center justify-content-center p-2">
            <span className="round-8 text-bg-primary rounded-circle me-2 d-inline-block"></span>
            <h6 className="mb-0">
              <strong>This Week:</strong>{" "}
              <span className="text-muted">{summary.week} Cases</span>
            </h6>
          </div>
          <div className="d-flex align-items-center justify-content-center p-2">
            <span className="round-8 text-bg-primary rounded-circle me-2 d-inline-block"></span>
            <h6 className="mb-0">
              <strong>This Month:</strong>{" "}
              <span className="text-muted">{summary.month} Cases</span>
            </h6>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasesTransportedCard;